import { useFormContext } from 'react-hook-form';
import {
  ClientRelationshipStageWithUndefined,
  ShareResultsSwitch,
  FieldLabel,
} from '@client';
import { SelectClientRelationshipStage } from './SelectClientRelationshipStage';
import { SelectReferralSource } from './SelectReferralSource';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

interface OptionalSettingsFieldsProps {
  className?: string;
  accordionClassName?: string;
  contentClassName?: string;
  borderClassName?: string;
  hideRelationshipStage?: boolean;
}

export function OptionalSettingsFields({
  className,
  accordionClassName,
  contentClassName,
  borderClassName = 'border border-gray-200',
  hideRelationshipStage = false,
}: OptionalSettingsFieldsProps) {
  const { watch, setValue } = useFormContext();

  const sendClientResultsLink = watch('sendClientResultsLink') || false;
  const clientRelationshipStage = watch('clientRelationshipStage');
  const selectedReferralSourceId = watch('selectedReferralSourceId');

  const handleSendClientResultsLinkChange = (checked: boolean) => {
    setValue('sendClientResultsLink', checked);
  };

  const handleClientRelationshipStageChange = (
    value: ClientRelationshipStageWithUndefined,
  ) => {
    setValue(
      'clientRelationshipStage',
      value === 'undefined' ? undefined : value,
    );
  };

  const handleReferralSourceIdChange = (value: string | undefined) => {
    setValue('selectedReferralSourceId', value);
  };

  return (
    <div className="flex flex-col gap-8">
      <Accordion
        type="single"
        collapsible
        className={cn('mt-2 rounded-md', borderClassName, accordionClassName)}
      >
        <AccordionItem
          value="optional-fields"
          className="text-sm"
        >
          <AccordionTrigger
            className={cn(
              'inline-flex items-center text-sm font-semibold text-gray-700',
              'data-[state=open]:border-b data-[state=open]:border-primary-200',
            )}
            withBackground={false}
            data-testid="optional-fields-trigger"
          >
            <FieldLabel
              text="Optional fields"
              className="my-0"
            />
          </AccordionTrigger>
          <AccordionContent
            className={cn('mb-2 mt-2 px-3 py-3', contentClassName)}
          >
            <div className={cn('flex flex-col gap-4', className)}>
              {/* Assign Type */}
              {!hideRelationshipStage && (
                <div className="flex flex-col gap-2">
                  <FieldLabel text="Assign Type" />
                  <SelectClientRelationshipStage
                    clientRelationshipStage={clientRelationshipStage}
                    onChange={handleClientRelationshipStageChange}
                    triggerClassName="text-gray-800"
                  />
                </div>
              )}

              {/* Referral Source */}
              <div className="flex flex-col gap-2">
                <SelectReferralSource
                  selectedReferralSourceId={selectedReferralSourceId}
                  selectValueClassName="text-primary-dark"
                  onChangeReferralSourceId={handleReferralSourceIdChange}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Share Results */}
      <ShareResultsSwitch
        value={sendClientResultsLink}
        onChange={handleSendClientResultsLinkChange}
      />
    </div>
  );
}
